import { Type } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { assign } from '../../utils/js.utils';
import { createSymbol } from '../../utils/symbol';
import { RenderingContext } from './../../../api';
import {
  arrayPush,
  identity,
  isFunction,
  isNil,
  isObservable,
  isPlainObject,
  isString,
  isStringArray,
  perfMeasure,
  safeUnsubscribeAll,
  UNDEFINED_RENDERING_CONTEXT,
  wchDecodeExpression
} from './../../../utils';
import {
  assertIsFunction,
  assertIsString,
  assertNotNil,
  isAssertOn
} from './../../internal/assert';
import { li, lw } from './../../services/logger/logger.service';
import {
  mappingRegisterFromLayoutComponent,
  mappingRegisterFromMappingDirective
} from './../../utils/component.mapping.utils';
import {
  cmpAddAnnotation,
  cmpRegisterComponent
} from './../../utils/component.utils';
import { KEY_ON_RENDERING_CONTEXT } from './../../utils/wch.utils';
import {
  LayoutComponentDirective,
  LayoutMappingDirective,
  RenderingContextDirective
} from './layout.directive';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'LayoutDecorator';
const _BehaviorSubject = BehaviorSubject;

const KEY_OBSERVABLE_PREFIX = 'on';
const KEY_ON_DESTROY = 'ngOnDestroy';
const KEY_SUBSCRIPTIONS = createSymbol();

// can't use a symbol since we use this aross libraries
const KEY_EXPRESSION = '45b01348-de92-44a0-8103-7b7dc471ad8c';

type Subscriptions = Subscription[];

interface Binding<T> {
  name: string;
  observable: Observable<T>;
  subscription: Subscription;
  value: T;
}

const _SYMBOLS: Record<string, symbol | string> = {};

/*
 * Construct a new, local symbol for our name
 *
 * @param aName     the name
 * @return the symbol for the name
 */
function _getSymbol(aName: string): symbol | string {
  return _SYMBOLS[aName] || (_SYMBOLS[aName] = createSymbol());
}

/*
 * Cache for expressions to save on parsing overhead
 */
const _expressionCache: { [key: string]: RenderingContextDirective<any> } = {};

/*
 * Parses a simple parameter expression separated by dots
 *
 * @param aExpression the expression
 * @return the function that executs this expression
 */
function _parseExpression(aExpression: string): RenderingContextDirective<any> {
  // sanity check
  if (isAssertOn()) {
    assertNotNil(aExpression);
    assertIsString(aExpression);
  }
  // check if we know it
  const cachedResult = _expressionCache[aExpression];
  if (isFunction(cachedResult)) {
    // the result cache
    return cachedResult;
  }
  // parse
  if (isString(aExpression)) {
    // parse the beast
    const tokens = aExpression.trim().split('.');
    const len = tokens.length;
    // returns the function
    const newResult: RenderingContextDirective<any> = rc => {
      // length
      let result: any = rc;
      for (let i = 0; i < len; ++i) {
        result = isPlainObject(result) ? result[tokens[i]] : undefined;
      }
      // ok
      return result;
    };
    // update
    return (_expressionCache[aExpression] = newResult);
  } else {
    // warn
    lw(LOGGER, 'Expression must be a string:', aExpression);
  }
  // default
  return identity;
}

/*
 * Registers a new accessor for the rendering context
 *
 * @param aPrototype    prototype
 * @param aPropertyKey  property key
 * @param aHandler  the expression
 * @param aDefault the optional default
 */
function _registerExpression<T>(
  aPrototype: any,
  aPropertyKey: string,
  aExpression: string,
  aDefault?: T
) {
  // dispatch
  return _registerRenderingContextDirective(
    aPrototype,
    aPropertyKey,
    aExpression,
    _parseExpression(aExpression),
    aDefault
  );
}

/**
 * Makes sure we have a binding
 *
 * @param aInstance     the instance to attach the binding to
 * @param aSymbol       the symbol
 * @param aName         name of the object
 *
 * @return the binding
 */
function _assertBinding<T>(
  aInstance: any,
  aSymbol: symbol | string,
  aName: string
): Binding<T> {
  // make sure we have a binding
  return aInstance[aSymbol] || (aInstance[aSymbol] = { name: aName });
}

const UNDEFINED_RENDERING_CONTEXT_SUBJECT = new _BehaviorSubject(
  UNDEFINED_RENDERING_CONTEXT
);

function _getOnRenderingContext(aInstance: any): Observable<RenderingContext> {
  // construct the observable
  const onRc = aInstance[KEY_ON_RENDERING_CONTEXT];
  if (isObservable<RenderingContext>(onRc)) {
    return onRc;
  }
  // warn
  lw(
    LOGGER,
    'Object ',
    aInstance,
    'must expose an observable for',
    KEY_ON_RENDERING_CONTEXT,
    '.'
  );
  // fallback
  return UNDEFINED_RENDERING_CONTEXT_SUBJECT;
}

/*
 * Constructs a new observable expression or reuses the previously created one
 *
 * @param aInstance the instance
 * @param aSymbol   the symbol
 * @param aName     name of the expression
 * @param aHandler  the handler
 *
 * @return an observable  that produces the desired value
 */
function _getObservableExpression<T>(
  aInstance: any,
  aSymbol: symbol | string,
  aName: string,
  aHandler: RenderingContextDirective<T>
): Observable<T> {
  // check if we have an observable
  const binding = _assertBinding<T>(aInstance, aSymbol, aName);
  let ob = binding.observable;
  if (isNil(ob)) {
    // validate
    if (isAssertOn()) {
      assertIsFunction(aHandler);
    }
    // console.log('initializing observable', aName);
    // attach
    ob = binding.observable = _getOnRenderingContext(aInstance).pipe(
      map(aHandler),
      // cache the last value
      shareReplay(1)
    );
  }
  // ok
  return ob;
}

/*
 * Registers a subscription, so we can unsubscribe as soon as ngOnDestroy is invoked.
 *
 * @param aInstance         the instance
 * @param aSubscription     the subscription
 */
function _registerSubscription(
  aInstance: any,
  aSubscription: Subscription
): void {
  // check if we have initialized the instance
  let sub = aInstance[KEY_SUBSCRIPTIONS] as Subscriptions;
  if (isNil(sub)) {
    // new subscriptions
    sub = aInstance[KEY_SUBSCRIPTIONS] = [];
    // override the ngOnDestroy method
    const oldDestroy = aInstance[KEY_ON_DESTROY];
    aInstance[KEY_ON_DESTROY] = () => {
      // undo all subscriptions
      safeUnsubscribeAll(sub);
      // dispatch
      if (isFunction(oldDestroy)) {
        // delegate
        oldDestroy.apply(aInstance);
      }
    };
  }
  // register
  arrayPush(aSubscription, sub);
}

/*
 * Decodes a plain value from a binding. We will either attach to the rendering context using the
 * binding handler or try to fallback to the Observable.
 *
 * @param aInstance     the instance
 * @param aSymbol       symbol for this binding
 * @param aName         base name of the binding
 * @param aObserableName    observable name of the binding
 * @param aHandler  the handler or null
 *
 * @return the resolved symbol
 */
function _getResolvedExpression<T>(
  aInstance: any,
  aSymbol: symbol | string,
  aBaseName: string,
  aObserableName: string,
  aHandler?: RenderingContextDirective<T>
): T {
  // check if we have a subscription
  const binding = _assertBinding<T>(aInstance, aSymbol, aBaseName);
  if (isNil(binding.subscription)) {
    // the new subscription
    let sub;
    // test if we have a handler, in this case create a new observable
    if (isFunction(aHandler)) {
      // construct the observable
      sub = _getOnRenderingContext(aInstance)
        .pipe(map(aHandler))
        .subscribe(val => (binding.value = val));
    } else {
      // check if we have an observable to attach to
      const ob = aInstance[aObserableName];
      if (isObservable<T>(ob)) {
        // attach
        sub = ob.subscribe(val => (binding.value = val));
      } else {
        // warn
        lw(LOGGER, 'Binding for', aBaseName, 'cannot be resolved.');
        // dummy subscription
        sub = UNDEFINED_RENDERING_CONTEXT_SUBJECT.subscribe();
      }
    }
    // register this subscription
    _registerSubscription(aInstance, (binding.subscription = sub));
  }
  // return the value
  return binding.value;
}

/*
 * Tests if a string is completely upper case
 *
 * @param aValue    the value to test
 * @return true if the value is upper case, else false
 */
function _isUpperCase(aValue: string): boolean {
  return aValue.toLowerCase().toUpperCase() === aValue;
}

/*
 * Tests if the name of the variable represents an observable expression
 *
 * @param aKey  the key
 * @return true, if the name starts with 'onX' and 'X' is upper case
 */
function _isObservableExpression(aKey: string): boolean {
  // test prefix
  return (
    aKey.length > KEY_OBSERVABLE_PREFIX.length &&
    aKey.indexOf(KEY_OBSERVABLE_PREFIX) === 0 &&
    _isUpperCase(aKey[KEY_OBSERVABLE_PREFIX.length])
  );
}

/*
 * Converts an observable expression to a normal expression. We assume
 * that the given key is of the form 'onX'
 *
 * @param aKey  the observable expression key
 * @return the original expression
 */
function _baseExpressionFromObservableExpression(aKey: string): string {
  return (
    aKey[KEY_OBSERVABLE_PREFIX.length].toLowerCase() +
    aKey.substr(KEY_OBSERVABLE_PREFIX.length)
  );
}

/*
 * Converts a normal expression to an observable expression. The expression will have the 'onX' prefix.
 *
 * @param aKey  the base expression key
 * @return the original expression
 */
function _observableExpressionFromBaseExpression(aKey: string): string {
  return KEY_OBSERVABLE_PREFIX + aKey[0].toUpperCase() + aKey.substr(1);
}

// gets an expression
type ExpressionGetter = () => string;

/**
 * Returns an expression from its descriptor
 *
 * @param aPrototype    the prototype
 * @param aOtherKey     the key
 *
 * @return the expression, null if no expression exists and undefined if we need to fallback
 */
function _getExpressionFromDescriptor(
  aPrototype: any,
  aOtherKey: string
): string | null | undefined {
  // access the descriptor
  const desc = Object.getOwnPropertyDescriptor(aPrototype, aOtherKey);
  if (desc) {
    const getter = desc.get;
    if (isFunction(getter)) {
      // the getter
      const eg = getter[KEY_EXPRESSION] as ExpressionGetter;
      if (isFunction(eg)) {
        // invoke
        return eg();
      }
    }
  }
  // nothing
  return undefined;
}

/**
 * Checks if we can find an expression from the other property
 *
 * @param aPrototype    the prototype
 * @param aOtherKey     the other key
 *
 * @return the expression, null if no expression exists and undefined if we need to fallback
 */
function _getExpressionFromPrototype(
  aPrototype: any,
  aOtherKey: string
): string | null | undefined {
  // loop
  let obj = aPrototype;
  while (obj) {
    // check
    const exp = _getExpressionFromDescriptor(obj, aOtherKey);
    if (exp !== undefined) {
      return exp;
    }
    // fallback
    obj = Object.getPrototypeOf(obj);
  }
  // nothing to be found
  return null;
}

/**
 * Returns a getter method that computes the expression associated with a property
 *
 * @param aPrototype current prototype
 * @param aExpression the expression, may be null or undefined
 * @param aOtherKey the alternative key
 *
 * @return the getter. The getter returns the expression or null
 */
function _getExpressionGetter(
  aPrototype: any,
  aExpression: string | null | undefined,
  aKey: string,
  aOtherKey: string
): ExpressionGetter {
  // current expression, compute lazily
  let expression;

  return () => {
    // lazily compute
    if (expression === undefined) {
      // check if we have some data
      if (aExpression) {
        expression = wchDecodeExpression(aExpression);
      } else {
        // protect against cycles
        expression = null;
        // try to load
        expression = _getExpressionFromPrototype(aPrototype, aOtherKey);
      }
      // log this
      li(LOGGER, 'Computed expression for key', aKey, expression);
    }
    // ok
    return expression;
  };
}

/*
 * Registers a new handler for the rendering context
 *
 * @param aPrototype    the instance
 * @param aPropertyKey  property key
 * @param aHandler  the handler
 * @param aDefault the optional default
 */
function _registerRenderingContextDirective<T>(
  aPrototype: any,
  aPropertyKey: string,
  aExpression: string | null,
  aHandler?: RenderingContextDirective<T>,
  aDefault?: T
) {
  // some sanity check
  if (isAssertOn()) {
    assertNotNil(aPrototype);
    assertIsString(aPropertyKey);
  }
  // test the kind of expression
  const bIsObservable = _isObservableExpression(aPropertyKey);
  // the keys
  let baseExpression: string;
  let observableExpression: string;
  if (bIsObservable) {
    observableExpression = aPropertyKey;
    baseExpression = _baseExpressionFromObservableExpression(
      observableExpression
    );
  } else {
    baseExpression = aPropertyKey;
    observableExpression = _observableExpressionFromBaseExpression(
      baseExpression
    );
  }
  // hidden symbold for this property, so we can check for lazy initialization
  const key = _getSymbol(baseExpression);
  // register our handler
  const oldHandler = aHandler;
  let newHandler: RenderingContextDirective<T>;
  if (isFunction(oldHandler) && aDefault !== undefined) {
    // wrap the handler into a default fallback
    newHandler = rc => oldHandler(rc) || aDefault;
  } else {
    // use as is
    newHandler = oldHandler;
  }
  // the getter
  const getter = bIsObservable
    ? function() {
        return _getObservableExpression(this, key, baseExpression, newHandler);
      }
    : function() {
        return _getResolvedExpression(
          this,
          key,
          baseExpression,
          observableExpression,
          newHandler
        );
      };
  // getter
  const expGetter = _getExpressionGetter(
    aPrototype,
    aExpression,
    aPropertyKey,
    bIsObservable ? baseExpression : observableExpression
  );
  // add some magic to the getter
  getter[KEY_EXPRESSION] = expGetter;
  // define a new property
  Object.defineProperty(aPrototype, aPropertyKey, {
    get: getter,
    enumerable: true,
    configurable: false
  });
}

/*
 * Layout decorator and metadata.
 *
 * @Annotation
 */
export function LayoutComponent(
  aDirective?: LayoutComponentDirective
): (cls: Type<any>) => Type<any> {
  // fix the directive
  const directive = aDirective || {};
  // export the decorator function
  return function(aClass: Type<any>): Type<any> {
    // register
    cmpAddAnnotation(directive, aClass);
    // measure performance
    const handle = perfMeasure('@LayoutComponent');
    // register this component
    cmpRegisterComponent(directive, aClass);
    // register a potential mapping
    mappingRegisterFromLayoutComponent(directive, aClass);
    // done
    handle();
    // return new constructor (will override original)
    return aClass;
  };
}

/*
 * Decorator for a property to represent a resolved property from the rendering context
 *
 * @param aBinding  the binding, a simple property expression relative to the rendering context
 * @param aDefault an optional default value if the binding does not exist
 *
 * @Annotation
 */
export function RenderingContextBinding<T>(
  aBinding?: string | RenderingContextDirective<T>,
  aDefault?: T
): (target: Object, propertyKey: string) => void {
  // return the function
  return (target: any, propertyKey: string) =>
    isString(aBinding)
      ? _registerExpression(target, propertyKey, aBinding, aDefault)
      : _registerRenderingContextDirective(
          target,
          propertyKey,
          null,
          aBinding,
          aDefault
        );
}

/**
 * Tests if a value is a string or a string array
 */
function _isStringOrArray(aValue: any): aValue is string | string[] {
  return isString(aValue) || isStringArray(aValue);
}

/*
 * Layout mapping decorator and metadata
 *
 * @Annotation
 */
export function LayoutMapping(
  aID: string | string[] | LayoutMappingDirective,
  aSelector?: string | string[] | Type<any>,
  aLayoutMode?: string | string[]
): (cls: Type<any>) => void {
  // export the decorator function
  return (cls: Type<any>) => {
    // measure performance
    const handle = perfMeasure('@LayoutMapping');
    // init the structure
    const directive: LayoutMappingDirective = {};
    if (_isStringOrArray(aID)) {
      directive.id = aID;
    } else if (isPlainObject(aID)) {
      assign(directive, aID);
    }
    // augment the selector
    if (_isStringOrArray(aSelector)) {
      directive.selector = aSelector;
    }
    // augment the mode
    if (_isStringOrArray(aLayoutMode)) {
      directive.layoutMode = aLayoutMode;
    }
    // register
    cmpAddAnnotation(directive, cls);
    // dispatch
    mappingRegisterFromMappingDirective(directive, cls);
    // done
    handle();
  };
}
